import { msg } from "@lingui/core/macro";
import { z } from "zod";
import * as Sentry from "@sentry/react";
import { i18n } from "@lingui/core";
import type { MessageDescriptor } from "@lingui/core";

export const knownErrorCodeSchema = z.enum([
  // Common
  "InvalidJson",
  "InvalidLatvianPersonasKods",
  "InvalidPatientType",
  "InvalidSwedishPersonalNumber",
  "InvalidSwedishTemporaryNumber",
  "InvalidSwissEMedicusNumber",
  "InvalidSwissPatientNumber",
  "PreconditionFailed",
  "UnhandledException",
  // Logistics
  "ActivityClosed",
  "ActivityNotFound",
  "AdditionalAddressInformationIsRequired",
  "AddressLine1MustBeSet",
  "AlreadyTravelledToVisit",
  "AlreadyTravellingToVisit",
  "AtLeastOneRelativeIsRequired",
  "AuthenticationFailed",
  "CityMustBeSet",
  "ConflictingOccurrence",
  "DiagnosisIsRequired",
  "DuplicateCareEpisodeId",
  "EmployeeNotFound",
  "FinishedVisit",
  "HasActiveCareEpisode",
  "HealthcareJourneyNumberIsRequired",
  "HomeCareIsRequired",
  "InformationIsRequired",
  "InvalidActivityCategory",
  "InvalidArea",
  "InvalidDate",
  "InvalidDateTime",
  "InvalidEndDate",
  "InvalidLatitude",
  "InvalidLongitude",
  "InvalidName",
  "InvalidPatientStatus",
  "InvalidPostalCode",
  "InvalidRelativeName",
  "InvalidRouteStatus",
  "InvalidStartDate",
  "InvalidStartTime",
  "InvalidTime",
  "InvalidVisitStatus",
  "InvalidWard",
  "KeyToPatientIsRequired",
  "LifeCareIsRequired",
  "MissingCompetence",
  "MissingHeader",
  "MissingRoute",
  "MissingSiteIdParameter",
  "NoOccurrencesInSchedule",
  "OccurrenceFinished",
  "OccurrenceGroupNotFound",
  "OccurrenceGroupNotStarted",
  "OccurrenceNotAssigned",
  "OccurrenceNotAllocated",
  "OccurrenceNotFinished",
  "OccurrenceNotStarted",
  "OccurrenceOutsideOfSchedule",
  "OccurrenceNotFound",
  "OccurrencePassed",
  "OccurrenceRemoved",
  "OngoingVisit",
  "PatientNotFound",
  "PetIsRequired",
  "RelationNotSet",
  "RelativeIsRequired",
  "SafetyAlarmTextIsRequired",
  "SiteNotAuthorized",
  "SiteNotFound",
  "SwedishTemporaryIdentityNumberFeatureDisabled",
  "TeamNotFound",
  "UnstartedTripToVisit",
  "UnstartedVisit",
  "VisitNotFound",
  "NotSameDay",
  "HandledByInvoicing",
  "AlreadyPlanned",
  "ShiftMissing",
  "InvalidAssignee",
  // Patient
  "AdmissionDateBeforeRegistration",
  "AdmissionDateInFuture",
  "AlreadyAdmitted",
  "AlreadyRegistered",
  "CannotDischargeInFuture",
  "CloseContactIsRequired",
  "DischargeDayBeforeAdmitted",
  "DuplicateHealthcareJourneyNumber",
  "ExternalCareEpisodeIdIsRequired",
  "FlagsIsRequired",
  "FunctionIsNotActive",
  "InvalidCoordinates",
  "InvalidDiagnosisCode",
  "InvalidEconomicUnit",
  "InvalidEdit",
  "InvalidField",
  "InvalidHealthcareJourneyNumber",
  "InvalidPhoneNumber",
  "InvalidSafetyAlarm",
  "InvalidTeam",
  "IsReturningPatient",
  "PatientNotFound",
  "RelativeIdConflict",
  "RelationMissing",
  "RelativeMissing",
  "RelativeNameMissing",
  "RequiredField",
  "TooFarAwayFromCareProvider",
  "TooFarFromCareHub",
]);
type KnownErrorCode = z.infer<typeof knownErrorCodeSchema>;
const knownErrorCodeDictionary: Record<
  KnownErrorCode,
  {
    issue: MessageDescriptor | "DEFAULT";
    resolution: MessageDescriptor | "NONE" | "DEFAULT";
  }
> = {
  // Common
  InvalidJson: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidLatvianPersonasKods: {
    issue: msg`Ogiltig personas kods.`,
    resolution: msg`Ange en giltig personas kods på formen XXXXXX-XXXXX.`,
  },
  InvalidPatientType: {
    issue: msg`Ogiltig patienttyp.`,
    resolution: "DEFAULT",
  },
  InvalidSwedishPersonalNumber: {
    issue: msg`Ogiltigt personnummer.`,
    resolution: msg`Ange ett giltigt personnummer på formen ÅÅÅÅMMDDXXXX.`,
  },
  InvalidSwedishTemporaryNumber: {
    issue: msg`Ogiltigt reservnummer.`,
    resolution: msg`Ange ett giltigt reservnummer bestående av siffror och/eller bokstäver.`,
  },
  InvalidSwissEMedicusNumber: {
    issue: msg`Ogiltigt eMedicus-nummer.`,
    resolution: msg`Ange ett giltigt 6-siffrigt eMedicus-nummer.`,
  },
  InvalidSwissPatientNumber: {
    issue: msg`Ogiltigt patientnummer.`,
    resolution: msg`Ange ett giltigt patientnummer med 7-10 siffror.`,
  },
  PreconditionFailed: {
    issue: msg`Detta har ändrats sedan sidan laddades in.`,
    resolution: msg`Ladda om sidan och försök igen.`,
  },
  UnhandledException: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },

  // Logistics
  ActivityClosed: {
    issue: msg`Åtgärden kan inte utföras eftersom aktiviteten har avslutats.`,
    resolution: "NONE",
  },
  ActivityNotFound: {
    issue: msg`Aktiviteten kan inte hittas.`,
    resolution: msg`Ladda om sidan och prova igen.`,
  },
  AdditionalAddressInformationIsRequired: {
    issue: msg`Övrig adressinformation krävs.`,
    resolution: msg`Ange övrig adressinformation och försök igen.`,
  },
  AddressLine1MustBeSet: {
    issue: msg`Adress krävs.`,
    resolution: msg`Ange en adress och försök igen.`,
  },
  AlreadyTravelledToVisit: {
    issue: msg`Resan till besöket är redan genomförd.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  AlreadyTravellingToVisit: {
    issue: msg`Resan till besöket är redan påbörjad.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  AtLeastOneRelativeIsRequired: {
    issue: msg`Patienten måste ha minst en anhörig.`,
    resolution: msg`Lägg till en annan anhörig innan du tar bort den här.`,
  },
  AuthenticationFailed: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  CityMustBeSet: {
    issue: msg`Postort krävs.`,
    resolution: msg`Ange en postort och försök igen.`,
  },
  ConflictingOccurrence: {
    issue: msg`Det går inte att flytta aktiviteten till en tid som överlappar med ett annat tillfälle i serien.`,
    resolution: msg`Välj en tid som inte krockar med andra tillfällen.`,
  },
  DiagnosisIsRequired: {
    issue: msg`Diagnoskod krävs.`,
    resolution: msg`Ange en diagnoskod och försök igen.`,
  },
  DuplicateCareEpisodeId: {
    issue: msg`Det finns redan en vårdperiod med samma id.`,
    resolution: msg`Avsluta den aktiva vårdperioden innan du försöker skapa en ny.`,
  },
  EmployeeNotFound: {
    issue: msg`Medarbetaren kunde ej hittas.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  FinishedVisit: {
    issue: msg`Besöket är slutfört.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  HasActiveCareEpisode: {
    issue: msg`Patienten har en pågående vårdperiod.`,
    resolution: msg`Avsluta den pågående vårdperioden innan du försöker starta en ny.`,
  },
  HealthcareJourneyNumberIsRequired: {
    issue: msg`Sjukresenummer krävs.`,
    resolution: msg`Ange ett giltigt sjukresenummer och försök igen.`,
  },
  HomeCareIsRequired: {
    issue: msg`Information om sociala insatser krävs.`,
    resolution: msg`Lägg till information om sociala insatser och försök igen.`,
  },
  InformationIsRequired: {
    issue: msg`Allmän information krävs.`,
    resolution: msg`Lägg till allmän information och försök igen.`,
  },
  InvalidActivityCategory: {
    // Won't happen unless developer error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidArea: {
    issue: msg`Ogiltigt område.`,
    resolution: "DEFAULT",
  },
  InvalidDate: {
    issue: msg`Ogiltigt datum.`,
    resolution: "NONE",
  },
  InvalidDateTime: {
    // Behind the scenes.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidEndDate: {
    issue: msg`Ogiltigt slutdatum.`,
    resolution: "NONE",
  },
  InvalidLatitude: {
    issue: msg`Adressen är ogiltig.`,
    resolution: msg`Kontrollera att adressen är korrekt och att nålen är placerad på rätt plats på kartan.`,
  },
  InvalidLongitude: {
    issue: msg`Adressen är ogiltig.`,
    resolution: msg`Kontrollera att adressen är korrekt och att nålen är placerad på rätt plats på kartan.`,
  },
  InvalidName: {
    // Is used for changing route namn at time of commit.
    // Too generic name.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidPatientStatus: {
    // Behind the scenes.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidPostalCode: {
    issue: msg`Ogiltigt postnummer.`,
    resolution: msg`Ange ett giltigt postnummer.`,
  },
  InvalidRelativeName: {
    issue: msg`Ogiltigt namn på anhörig.`,
    resolution: msg`Ange ett giltigt namn för den anhöriga.`,
  },
  InvalidRouteStatus: {
    // Behind the scenes.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidStartDate: {
    issue: msg`Angivet datum har redan passerat.`,
    resolution: msg`Ange ett datum i framtiden.`,
  },
  InvalidStartTime: {
    issue: msg`Angiven tidpunkt har redan passerat.`,
    resolution: msg`Ange en tid i framtiden och försök igen.`,
  },
  InvalidTime: {
    issue: msg`Ogiltigt tidsformat.`,
    resolution: "NONE",
  },
  InvalidVisitStatus: {
    // Behind the scenes
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidWard: {
    issue: msg`Ogiltig avdelning.`,
    resolution: "DEFAULT",
  },
  KeyToPatientIsRequired: {
    issue: msg`Information av nyckel krävs.`,
    resolution: msg`Ange information om nyckel och försök igen.`,
  },
  LifeCareIsRequired: {
    issue: msg`LifeCare krävs.`,
    resolution: msg`Lägg till LifeCare och försök igen.`,
  },
  MissingCompetence: {
    issue: msg`Aktiviteten saknar kompetensbehov.`,
    resolution: msg`Ange minst en kompetens.`,
  },
  MissingHeader: {
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  MissingRoute: {
    issue: msg`Rutten finns inte.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  MissingSiteIdParameter: {
    // Programmer error — the client should always send `siteId`.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  NoOccurrencesInSchedule: {
    issue: msg`Schemat resulterar inte i några aktivitetstillfällen.`,
    resolution: msg`Se över start- och slutdatum och/eller frekvens så att minst ett tillfälle ryms i schemat.`,
  },
  OccurrenceFinished: {
    issue: msg`Aktiviteten har avslutats.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OccurrenceGroupNotFound: {
    issue: msg`Hittade inte besöket.`,
    resolution: msg`Ladda om sidan och försök igen.`,
  },
  OccurrenceGroupNotStarted: {
    issue: msg`Det går inte att avsluta besöket innan dess starttid.`,
    resolution: msg`Vänta tills besökets starttid har passerat och försök igen.`,
  },
  OccurrenceNotAssigned: {
    issue: msg`Aktiviteten har inte tilldelats någon.`,
    resolution: "NONE",
  },
  OccurrenceNotAllocated: {
    issue: msg`Aktiviteten har inte tilldelats någon.`,
    resolution: msg`Lägg till den som utförde aktiviteten och försök igen.`,
  },
  OccurrenceNotFinished: {
    issue: msg`Aktivitetstillfället är inte avslutat.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OccurrenceNotStarted: {
    issue: msg`Det går inte att avsluta besöket innan dess starttid.`,
    resolution: msg`Vänta tills besökets starttid har passerat och försök igen.`,
  },
  OccurrenceOutsideOfSchedule: {
    issue: msg`Det går inte att flytta aktiviteten till den valda tiden eftersom den ligger utanför seriens start- och slutdatum.`,
    resolution: "NONE",
  },
  OccurrenceNotFound: {
    issue: msg`Hittade inte aktivitetstillfället.`,
    resolution: "DEFAULT",
  },
  OccurrencePassed: {
    issue: msg`Tiden för aktivitetstillfället har redan passerat.`,
    resolution: "NONE",
  },
  OccurrenceRemoved: {
    issue: msg`Aktivitetstillfället är borttaget.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  OngoingVisit: {
    issue: msg`Besök är pågående.`,
    resolution: msg`Prata med berörd kollega om ändringar ändå krävs.`,
  },
  PatientNotFound: {
    issue: msg`Hittade inte patienten.`,
    resolution: "DEFAULT",
  },
  PetIsRequired: {
    issue: msg`Information om husdjur krävs.`,
    resolution: msg`Lägg till information om husdjur och försök igen.`,
  },
  RelationNotSet: {
    issue: msg`Anhörigs relation till patienten saknas.`,
    resolution: msg`Ange den anhörigas relation till patienten.`,
  },
  RelativeIsRequired: {
    issue: msg`Anhörig krävs.`,
    resolution: msg`Lägg till en anhörig och försök igen.`,
  },
  SafetyAlarmTextIsRequired: {
    issue: msg`Säkerhetslarm krävs.`,
    resolution: msg`Lägg till säkerhetslarm och försök igen.`,
  },
  SiteNotAuthorized: {
    issue: msg`Du saknar behörighet till patientens enhet.`,
    resolution: msg`Kontakta din administratör om du behöver behörighet.`,
  },
  SiteNotFound: {
    issue: "DEFAULT",
    resolution: "NONE",
  },
  SwedishTemporaryIdentityNumberFeatureDisabled: {
    issue: msg`Funktionen för reservnummer är inaktiverad.`,
    resolution: "DEFAULT",
  },
  TeamNotFound: {
    issue: msg`Teamet kunde ej hittas.`,
    resolution: msg`Ladda om sidan och försök igen. Kontakta support om felet kvarstår.`,
  },
  UnstartedTripToVisit: {
    issue: msg`Resan till besöket har inte påbörjats.`,
    resolution: msg`Säkerställ att resan har påbörjats innan du fortsätter.`,
  },
  UnstartedVisit: {
    issue: msg`Besöket är inte påbörjat.`,
    resolution: msg`Säkerställ att besöket har påbörjats innan du fortsätter.`,
  },
  VisitNotFound: {
    issue: msg`Besöket hittades inte.`,
    resolution: msg`Stäm av med Center vid behov.`,
  },
  NotSameDay: {
    issue: msg`Besöket kan bara ångras samma dag som det slutfördes.`,
    resolution: "NONE",
  },
  HandledByInvoicing: {
    issue: msg`Besöket har redan hanterats av faktureringen och kan inte ångras.`,
    resolution: "NONE",
  },
  AlreadyPlanned: {
    issue: msg`Besöket är redan planerat.`,
    resolution: msg`Ladda om sidan för att se det senaste.`,
  },
  ShiftMissing: {
    issue: msg`Aktiviteten kunde inte markeras som utförd på grund av hur den är planerad.`,
    resolution: msg`Se över planeringen av aktiviteten i Center.`,
  },
  InvalidAssignee: {
    issue: msg`Aktiviteten kunde inte markeras som utförd på grund av hur den är planerad.`,
    resolution: msg`Se över planeringen av aktiviteten i Center.`,
  },

  // Patient
  AdmissionDateBeforeRegistration: {
    issue: msg`Inskrivningsdatumet är tidigare än när patienten registrerades.`,
    resolution: msg`Välj registreringsdagen eller en senare dag.`,
  },
  AdmissionDateInFuture: {
    issue: msg`Inskrivningsdatumet ligger i framtiden.`,
    resolution: msg`Välj dagens datum eller en tidigare dag.`,
  },
  AlreadyAdmitted: {
    issue: msg`Patienten är redan inskriven.`,
    resolution: "NONE",
  },
  CannotDischargeInFuture: {
    issue: msg`Datumet är i framtiden.`,
    resolution: msg`Ange ett datum som är idag eller tidigare.`,
  },
  DischargeDayBeforeAdmitted: {
    issue: msg`Datumet är tidigare än inskrivningsdatum.`,
    resolution: msg`Ange ett datum som är senare än inskrivningsdatum.`,
  },
  AlreadyRegistered: {
    issue: msg`Patienten är redan registrerad.`,
    resolution: "NONE",
  },
  CloseContactIsRequired: {
    issue: msg`Anhörig krävs.`,
    resolution: msg`Lägg till en anhörig och försök igen.`,
  },
  DuplicateHealthcareJourneyNumber: {
    issue: msg`Sjukresenumret används av en annan patient.`,
    resolution: msg`Ta ett nytt sjukresekort och försök igen.`,
  },
  ExternalCareEpisodeIdIsRequired: {
    issue: msg`Externt vårdperiodnummer krävs.`,
    resolution: msg`Ange ett externt vårdperiodnummer och försök igen.`,
  },
  FlagsIsRequired: {
    issue: msg`Flaggningar krävs.`,
    resolution: msg`Lägg till flaggningar och försök igen.`,
  },
  FunctionIsNotActive: {
    issue: msg`Funktionen är inte aktiv.`,
    resolution: "DEFAULT",
  },
  InvalidCoordinates: {
    issue: msg`Ogiltiga koordinater.`,
    resolution: msg`Latitud bör vara mellan -90 och 90, longitud mellan -180 och 180.`,
  },
  InvalidDiagnosisCode: {
    issue: msg`Ogiltig diagnoskod.`,
    resolution: msg`Dubbelkolla diagnoskoden och hör av dig till teknisk support om det borde fungera.`,
  },
  InvalidEconomicUnit: {
    issue: msg`Ogiltig ekonomisk enhet.`,
    resolution: msg`Dubbelkolla den ekonomiska enheten och hör av dig till teknisk support om det borde fungera.`,
  },
  InvalidEdit: {
    issue: msg`Ogiltig ändring.`,
    resolution: msg`Om du försökte ändra flera saker på samma gång, försök att ändra en sak i taget istället.`,
  },
  InvalidField: {
    issue: msg`Ogiltigt fält.`,
    resolution: "DEFAULT",
  },
  InvalidHealthcareJourneyNumber: {
    issue: msg`Ogiltigt sjukresenummer.`,
    resolution: msg`Kontrollera att sjukresenumret är korrekt och försök igen.`,
  },
  InvalidPhoneNumber: {
    issue: msg`Ogiltigt telefonnummer.`,
    resolution: msg`Kontrollera att telefonnumret är korrekt och försök igen.`,
  },
  InvalidSafetyAlarm: {
    // Empty string submitted. Doesn't happen and if it does it is an implementation error.
    issue: "DEFAULT",
    resolution: "DEFAULT",
  },
  InvalidTeam: {
    issue: msg`Ogiltigt team.`,
    resolution: msg`Kontrollera att teamet är ifyllt korrekt och försök igen.`,
  },
  IsReturningPatient: {
    issue: msg`Patienten har redan en vårdperiod hos er.`,
    resolution: msg`Återregistrera patienten i stället för att registrera en ny.`,
  },
  RelationMissing: {
    issue: msg`Anhörigs relation saknas.`,
    resolution: msg`Specificera den anhörigas relation till patienten.`,
  },
  RelativeIdConflict: {
    issue: msg`Anhörig med samma id existerar redan.`,
    resolution: msg`Ladda om sidan och försök igen.`,
  },
  RelativeMissing: {
    issue: msg`Anhörig hittades inte.`,
    resolution: "DEFAULT",
  },
  RelativeNameMissing: {
    issue: msg`Anhörig saknar namn.`,
    resolution: msg`Ange namn för den anhöriga.`,
  },
  RequiredField: {
    issue: msg`Får inte vara tomt.`,
    resolution: msg`Kontrollera att allt som inte är valfritt har fyllts i.`,
  },
  TooFarAwayFromCareProvider: {
    issue: msg`Adressen är för långt bort från vårdgivaren.`,
    resolution: "NONE",
  },
  TooFarFromCareHub: {
    issue: msg`Angiven adress ligger för långt bort.`,
    resolution: msg`Kontrollera att adressen är inom rimligt avstånd från den klinik eller enhet som är angiven som utgångspunkt.`,
  },
};

const internalErrorCodeSchema = z.object({
  data: z.object({
    code: knownErrorCodeSchema,
  }),
});

const internalErrorStatusSchema = z.object({
  response: z.object({
    status: z.number(),
  }),
});

export const isErrorWithKnownErrorCode = (
  error: unknown,
): error is { data: { code: KnownErrorCode } } => {
  return internalErrorCodeSchema.safeParse(error).success;
};

const isErrorWithStatus = (
  error: unknown,
): error is { response: { status: number } } => {
  return internalErrorStatusSchema.safeParse(error).success;
};

export const isClientSideError = (error: unknown) => {
  return (
    isErrorWithStatus(error) &&
    error.response.status >= 400 &&
    error.response.status < 499
  );
};

export const isUnauthenticatedError = (error: unknown) => {
  return isErrorWithStatus(error) && error.response.status === 401;
};

export const isUnauthorizedError = (error: unknown) => {
  return isErrorWithStatus(error) && error.response.status === 403;
};

export const deducedError = (error: unknown) => {
  const defaultUserFacingErrorExplanation = msg`Något gick fel.`;
  const defaultUserFacingErrorResolution = msg`Försök igen om en liten stund och hör av dig till teknisk support om det ändå inte fungerar.`;

  if (isErrorWithKnownErrorCode(error)) {
    const { issue, resolution } = knownErrorCodeDictionary[error.data.code];
    const userFacingErrorExplanation = i18n._(
      issue === "DEFAULT" ? defaultUserFacingErrorExplanation : issue,
    );
    const userFacingErrorResolution =
      resolution === "NONE"
        ? ""
        : resolution === "DEFAULT"
          ? i18n._(defaultUserFacingErrorResolution)
          : i18n._(resolution);
    return `${userFacingErrorExplanation}${userFacingErrorResolution ? ` ${userFacingErrorResolution}` : ""}`;
  }

  return `${i18n._(defaultUserFacingErrorExplanation)} ${i18n._(defaultUserFacingErrorResolution)}`;
};

export const displayErrorMessageAlert = (message: string) => {
  Sentry.captureException(new Error(`An error alert was shown: ${message}`));
  alert(message);
};
